import { Link } from 'react-router-dom'
import { Heart, CheckCircle, Clock, Award, Activity, Stethoscope } from 'lucide-react'
import Hero from '../../components/Hero'
import SEO from '../../components/SEO'

export default function Nursing() {
  const requirements = [
    'High school diploma with science subjects',
    'Minimum grade requirements in Biology and Chemistry',
    'Medical fitness certificate',
    'Police clearance certificate',
    'Two letters of recommendation',
    'Completed application form'
  ]

  const courses = [
    'Anatomy & Physiology',
    'Fundamentals of Nursing',
    'Medical-Surgical Nursing',
    'Maternal & Child Health',
    'Community Health Nursing',
    'Pharmacology',
    'Nursing Ethics & Professional Practice',
    'Clinical Practicum'
  ]

  const careers = [
    'Registered Nurse',
    'Clinical Nurse',
    'Community Health Nurse',
    'Maternal Health Specialist',
    'Pediatric Nurse',
    'Emergency Room Nurse',
    'Public Health Officer',
    'Healthcare Administrator'
  ]

  return (
    <>
      <SEO 
        title="Nursing Diploma - Rudovia Life College"
        description="Accredited nursing diploma program in Zimbabwe. Combine compassionate care with clinical excellence. Train to serve communities with professional healthcare."
        keywords="nursing diploma Zimbabwe, nursing program Africa, healthcare training, nursing college Zimbabwe, registered nurse training, clinical nursing"
      />
      <div className="pt-20">
        <Hero
          title="Nursing Diploma"
          subtitle="Combine compassionate care with clinical excellence"
          image="/assets/images/pexels-rdne-6129194.jpg"
          height="h-[400px]"
          primaryButton={{ text: 'Apply Now', link: '/admissions' }}
        />

        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <h2 className="heading-2 mb-6">Program Overview</h2>
                <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                  Our Nursing Diploma program provides comprehensive training in clinical nursing practice, combining theoretical knowledge with extensive hands-on experience. Students develop the skills, knowledge, and compassionate care approach needed to deliver quality healthcare in diverse settings.
                </p>
                <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                  Grounded in Christian values of service and compassion, our program prepares nurses who not only excel in clinical competence but also demonstrate genuine care for patients' physical, emotional, and spiritual well-being.
                </p>
                <div className="flex items-center space-x-8 mt-8">
                  <div className="flex items-center space-x-2">
                    <Clock className="w-6 h-6 text-secondary" />
                    <div>
                      <div className="font-bold text-primary">Duration</div>
                      <div className="text-gray-600">3 Years</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Award className="w-6 h-6 text-secondary" />
                    <div>
                      <div className="font-bold text-primary">Qualification</div>
                      <div className="text-gray-600">Diploma in Nursing</div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <img 
                  src="/assets/images/pexels-rdne-6129194.jpg" 
                  alt="Nursing Training" 
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gray-50 rounded-xl p-8">
                <Stethoscope className="w-12 h-12 text-secondary mb-4" />
                <h3 className="text-2xl font-bold text-primary mb-4">Core Courses</h3>
                <ul className="space-y-2">
                  {courses.map((course, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{course}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gray-50 rounded-xl p-8">
                <Activity className="w-12 h-12 text-secondary mb-4" />
                <h3 className="text-2xl font-bold text-primary mb-4">Admission Requirements</h3>
                <ul className="space-y-2">
                  {requirements.map((req, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{req}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gray-50 rounded-xl p-8">
                <Heart className="w-12 h-12 text-secondary mb-4" />
                <h3 className="text-2xl font-bold text-primary mb-4">Career Opportunities</h3>
                <ul className="space-y-2">
                  {careers.map((career, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{career}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding bg-primary text-white">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Why Study Nursing at Rudovia?</h2>
              <div className="grid md:grid-cols-2 gap-8 mt-12">
                <div className="text-left">
                  <h3 className="text-2xl font-bold mb-3 text-secondary">Clinical Excellence</h3>
                  <p className="text-gray-200">
                    Gain extensive clinical experience in hospitals and healthcare facilities, developing practical nursing skills.
                  </p>
                </div>
                <div className="text-left">
                  <h3 className="text-2xl font-bold mb-3 text-secondary">Compassionate Care</h3>
                  <p className="text-gray-200">
                    Learn to provide holistic care that addresses patients' physical, emotional, and spiritual needs.
                  </p>
                </div>
                <div className="text-left">
                  <h3 className="text-2xl font-bold mb-3 text-secondary">Expert Faculty</h3>
                  <p className="text-gray-200">
                    Learn from experienced registered nurses and healthcare professionals dedicated to your success.
                  </p>
                </div>
                <div className="text-left">
                  <h3 className="text-2xl font-bold mb-3 text-secondary">Accredited Program</h3>
                  <p className="text-gray-200">
                    Graduate with a recognized diploma that qualifies you for nursing registration and employment.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding bg-white">
          <div className="container-custom text-center">
            <h2 className="heading-2 mb-6">Start Your Nursing Career</h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Make a difference in people's lives through compassionate healthcare. Apply to our Nursing program today.
            </p>
            <Link to="/admissions" className="btn-primary">
              Apply Now
            </Link>
          </div>
        </section>
      </div>
    </>
  )
}
